import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { groupBy, isEmpty } from 'lodash';
import { AutoDismissFlag, FlagGroup } from '@atlaskit/flag';
import WarningIcon from '@atlaskit/icon/glyph/warning';
import Button, { LoadingButton } from '@atlaskit/button';
import Form, { CheckboxField, ErrorMessage } from '@atlaskit/form';
import { Checkbox } from '@atlaskit/checkbox';
import SectionMessage from '@atlaskit/section-message';
import OfficeBuildingFilledIcon from '@atlaskit/icon/glyph/office-building-filled';
import { createContactRelationship, updateInvoiceGroup, patchContactRelationship } from '@atlassian/temp-proxies';
import { ValueType } from '@atlaskit/select';
import { UpdateInvoiceGroupRequest, creditPolicyResponse } from '@atlassian/cssxp-types-billing';
import {
  Divider,
  errorNotificationBuilder,
  MoreActions,
  MoreActionsItem,
  NotificationTypes,
  useFeatureToggle,
  useNotification,
} from '@css-xp/base';
import { ENABLE_DESIGN_ITERATION_1, STATSIG_FEATURE_GATES } from '@css-xp/ppc-constants';
import {
  CommerceAddress,
  ContactInfo,
  EndCustomerContact,
  InvoiceGroup,
} from '@css-xp/graphtypes/src/generated-types-new';
import { getPartnerSubscriptionsUrl } from '@css-xp/ppc-navigation';
import { FormatCommerceAddress, ScreenEventAnalytics, getSupportUrl, Currency } from '@css-xp/ppc-common';
import { AnalyticsEventSource } from '@atlassian/temp-analytics';
import { legalLinks } from '@css-xp/ppc-consent-hub';
import { ADVOCATE_ACCESS_TYPE, ADVOCATE_RESOURCE_TYPE, AdvocateAccessWall } from '@css-xp/ppc-access-control';
import { useOpenClose } from '@css-xp/ppc-custom-hooks';
import { useContactServicePayload, useFetchEccFromContactService } from '@css-xp/contact-resources';
import {
  ContactType,
  CreateContactRelationshipsRequest,
  PatchContactRelationshipsRequest,
} from '@css-xp/contact-resources/src/constant';
import { getMaxLimitOfCustomerContact, isPaypalPaymentMethod } from '@css-xp/ppc-utils';
import { Estimate, CustomPanel } from '@css-xp/ppc-purchase-experience';
import { OrderInfoProps } from '@css-xp/ppc-purchase-experience/src/types';
import { QuoteOrderSummaryLineItem } from './quote-types';
import { LineItemsActualEstimate, BillingProfileActionsEnum } from './types';
import { OrderConfirmationDrawer } from './quote-order-confirmation';
import { BillingProfile } from './billing-profile';
import { LineItems } from './quote-line-items';
import { BillingProfileAndPONumberModal } from './billing-profile-po-number-modal';
import { BillingProfileOption } from './order-summary';
import { TermPaymentValidationError } from './term-payment-validation-error';
import './quote-order-summary.scss';

export const QUOTE_LINE_ITEM_TYPES = {
  ACCOUNT_MODIFICATION: 'ACCOUNT_MODIFICATION',
  CREATE_ENTITLEMENT: 'CREATE_ENTITLEMENT',
  AMEND_ENTITLEMENT: 'AMEND_ENTITLEMENT',
  REACTIVATE_ENTITLEMENT: 'REACTIVATE_ENTITLEMENT',
  CANCEL_ENTITLEMENT: 'CANCEL_ENTITLEMENT',
};

export type QuoteOrderSummaryProps = {
  lineItems: QuoteOrderSummaryLineItem[];
  transactionAccountId: string;
  invoiceGroupName?: string;
  lineItemsActualEstimate: LineItemsActualEstimate;
  billToAddressData: CommerceAddress;
  handleBackButtonClick: () => void;
  existingCustomerContacts?: {
    loading: boolean;
    data: Array<EndCustomerContact>;
  };
  isAddNewContactDisabled?: boolean;
  isExistingContactRemoveDisabled?: boolean;
  isCustomerContactHidden?: boolean;
  handleOrderSubmit: (_consentText: string) => Promise<void>;
  orderDetails?: OrderInfoProps;
  error: { title?: string; description: string } | null;
  setError: (_errorDetails: { title?: string; description: string } | null) => void;
  invoiceGroupDetails: InvoiceGroup | undefined;
  invoiceGroupDetailsLoading: boolean;
  areEstimatesRefreshing: boolean;
  isPollingFailed: boolean;
  termPaymentValidation: {
    response?: creditPolicyResponse;
    isLoading: boolean;
  };
  isDeferredPaymentIGSelectionBlocked: boolean;
  isPendingInvoice: boolean;
  checkFinancePolicies: () => Promise<creditPolicyResponse>;
  resetFinancePolicies: () => void;
  availableBillingProfilesForCustomer: InvoiceGroup[];
  billingProfilesLoading: boolean;
  refetchbillingProfilesForCustomer: () => void;
  containsTransferLineItems?: boolean;
  invoiceGroupId: string;
  setInvoiceGroupId: (_id: string) => void;
  quoteNumber?: string;
  quoteId?: string;
  setOrderDetails?: (_orderDetails: OrderInfoProps) => void;
  defaultCurrency?: string;
};

export const QuoteOrderSummary: React.FC<QuoteOrderSummaryProps> = ({
  lineItems,
  transactionAccountId,
  lineItemsActualEstimate,
  billToAddressData,
  handleBackButtonClick,
  isAddNewContactDisabled = false,
  isExistingContactRemoveDisabled = false,
  isCustomerContactHidden = false,
  handleOrderSubmit,
  orderDetails,
  error,
  setError,
  invoiceGroupDetails,
  invoiceGroupDetailsLoading,
  areEstimatesRefreshing,
  isPollingFailed,
  termPaymentValidation,
  isDeferredPaymentIGSelectionBlocked,
  isPendingInvoice,
  availableBillingProfilesForCustomer,
  billingProfilesLoading = false,
  refetchbillingProfilesForCustomer,
  checkFinancePolicies,
  resetFinancePolicies,
  containsTransferLineItems = false,
  invoiceGroupId,
  setInvoiceGroupId,
  quoteNumber,
  quoteId,
  setOrderDetails,
  defaultCurrency = Currency.USD,
}) => {
  const groupedLineItemsByCloudsite = groupBy(
    lineItems?.filter((lineItem) => lineItem?.cloudSite !== null),
    'cloudSite'
  );

  const groupLineItemsByOrgName = groupBy(
    lineItems?.filter((lineItem) => lineItem?.orgName !== null),
    'orgName'
  );

  const areAllTheAddedProductsFree = lineItems.every((lineItem) => lineItem.isFreeProduct);
  const selectedInvoiceGroupId = invoiceGroupId || invoiceGroupDetails?.id;
  let isDeactivatedBillingProfileSelected = false;

  const selectedBillingProfileDetails = selectedInvoiceGroupId
    ? availableBillingProfilesForCustomer?.find((invoiceGroup) => invoiceGroup?.id === selectedInvoiceGroupId)
    : null;

  const billingProfileInfo = selectedBillingProfileDetails || invoiceGroupDetails;

  const [transferConsent, setTransferConsent] = useState(false);
  const [isEnabled] = useFeatureToggle();
  const isAddOnChangesEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_ADD_ONS);
  const isProratedChangesEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_LOOM_OVERAGE);
  const isNonCommercialPricingEnable = isEnabled(STATSIG_FEATURE_GATES.ENABLE_NON_COMMERCIAL_PRICING);
  const shouldShowPrimaryContacts = isEnabled(STATSIG_FEATURE_GATES.ENABLE_CUSTOMER_CONTACT_V3);
  const isPaypalEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_PAYPAL_SUPPORT);
  const isAchPaymentMethodFFEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_ACH_DIRECT_DEBIT);
  const isFutureDatedEntitlementsEnabled = isEnabled(
    STATSIG_FEATURE_GATES.ENABLE_SUPPORT_FOR_FUTURE_DATED_ENTITLEMENTS
  );
  const isDesignIteration1Enabled = isEnabled(ENABLE_DESIGN_ITERATION_1);
  const isTWCPhase1Enabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_TWC_SUPPORT_PHASE_1);
  const isMultiInstanceAppsEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_MULTI_INSTANCE_APPS);

  isDeactivatedBillingProfileSelected = selectedBillingProfileDetails?.isActive === false;

  const { notificationSent, setNotificationSent, sendErrorNotification } = useNotification();
  const {
    getECCDetails,
    eccDetailsError,
    getQuotePrimaryCustomerContactDetail,
    getBulkECCDetails,
    bulkEccDetailsLoading,
  } = useFetchEccFromContactService();
  const {
    getCreateContactRelationshipsPayload,
    getPatchPayloadForSetPrimaryContact,
    getPatchPayloadForAddingAndRemovingPrimaryContact,
  } = useContactServicePayload();
  // insert default input field if no exisiting contact found required for the child component logic
  const [atlassianAccountIds, setAtlassianAccountIds] = useState(['']);
  const [atlassianAccountEmails, setAtlassianAccountEmails] = useState<Array<string>>(['']);
  const [endCustomerContacts, setEndCustomerContacts] = useState<Array<EndCustomerContact>>([]);
  const [eccLoading, setEccLoading] = useState<boolean>(true);
  const [isQuotePrimaryContactsLoading, setQuotePrimaryContactsLoading] = useState<boolean>(true);
  const [quotePrimaryContactsAaids, setQuotePrimaryContactsAaids] = useState<Array<string>>(['']);
  const [quotePrimaryContactsEmails, setQuotePrimaryContactsEmails] = useState<Array<string>>(['']);
  const [quotePrimaryContactName, setQuotePrimaryContactName] = useState<string>('');
  const [primaryContacts, setPrimaryContacts] = useState<ContactInfo[]>([]);
  const [allSecondaryCustomerContacts, setAllSecondaryCustomerContacts] = React.useState<ContactInfo[]>([]);
  const [hasAddedPrimaryContactInTheModal, setHasAddedPrimaryContactInTheModal] = React.useState(false);
  const [shouldReRenderBp, setShouldReRenderBillingProfile] = React.useState(true);
  const [hasAddedNewPrimaryContact, setHasAddedNewPrimaryContact] = React.useState(false);
  const [primaryContactsAaidForBp, setPrimaryContactsAaidForBp] = useState<Array<string>>(['']);
  const [primaryContactEmailsForBp, setPrimaryContactEmailsForBp] = useState<Array<string>>(['']);

  useEffect(() => {
    (async () => {
      setEccLoading(true);
      setQuotePrimaryContactsLoading(true);
      if (invoiceGroupId && !hasAddedPrimaryContactInTheModal) {
        let data = await getECCDetails(invoiceGroupId, transactionAccountId);
        if (shouldShowPrimaryContacts) {
          data = data.filter((contact: ContactInfo) => contact?.isPrimary);
        }
        setEndCustomerContacts(data);
        const existingContacts = data as EndCustomerContact[];
        const accountIds = existingContacts?.map(({ accountId }) => accountId);
        const accountEmails = existingContacts?.map(({ email, name }) => email || name);
        const existingAccountIds = accountIds && accountIds.length > 0 ? accountIds : [''];
        const existingAccountEmails = accountEmails && accountEmails.length > 0 ? accountEmails : [''];
        setAtlassianAccountIds(existingAccountIds);
        setAtlassianAccountEmails(existingAccountEmails);
      }
      if (shouldShowPrimaryContacts && quoteId) {
        const data = await getQuotePrimaryCustomerContactDetail({
          quoteId: quoteId,
          transactionAccountId: transactionAccountId,
          contactType: ContactType.CUSTOMER_CONTACT,
        });
        const contacts = data as EndCustomerContact[];
        const primaryContactsAaids = contacts?.map(({ accountId }) => accountId);
        const primaryContactsEmails = contacts?.map(({ email, name }) => email || name);
        primaryContactsAaids.length > 0 && setQuotePrimaryContactsAaids(primaryContactsAaids);
        primaryContactsEmails.length > 0 && setQuotePrimaryContactsEmails(primaryContactsEmails);
        setQuotePrimaryContactName(contacts[0]?.name);
      }
      setEccLoading(false);
      setQuotePrimaryContactsLoading(false);
    })();
  }, [invoiceGroupId, quoteId, hasAddedPrimaryContactInTheModal]);

  useEffect(() => {
    (async () => {
      if (shouldShowPrimaryContacts && !billingProfilesLoading) {
        const invoiceGroupIds = availableBillingProfilesForCustomer
          ?.filter((invoiceGroup) => invoiceGroup?.isActive)
          ?.map((invoiceGroup) => invoiceGroup?.id)
          ?.filter((id) => id !== undefined);

        const response = await getBulkECCDetails(invoiceGroupIds as string[], transactionAccountId);
        if (!isEmpty(response)) {
          const primaryContacts = response?.filter((contact: ContactInfo) => contact?.isPrimary);
          setPrimaryContacts(primaryContacts);
          const secondaryContacts = response?.filter((contact: ContactInfo) => !contact?.isPrimary);
          setAllSecondaryCustomerContacts(secondaryContacts);
        }
      }
    })();
  }, [billingProfilesLoading, shouldShowPrimaryContacts]);

  useEffect(() => {
    if (
      allSecondaryCustomerContacts
        ?.filter((contact: any) => contact?.identifierId === selectedBillingProfileDetails?.id)
        ?.find((contact: any) => contact?.accountId === atlassianAccountIds[0])
    ) {
      setHasAddedNewPrimaryContact(false);
    } else setHasAddedNewPrimaryContact(true);
  }, [atlassianAccountIds, invoiceGroupId]);

  const checkForAssociatedPaymentMethod = useCallback(
    (selectInvoiceGroupID: string) => {
      let billingProfileDetail = null;
      if (selectInvoiceGroupID) {
        if (availableBillingProfilesForCustomer.length > 0) {
          billingProfileDetail = availableBillingProfilesForCustomer?.find(
            (invoiceGroup) => invoiceGroup?.id === selectedInvoiceGroupId
          );
        } else if (invoiceGroupDetails?.id === selectedInvoiceGroupId) {
          billingProfileDetail = invoiceGroupDetails;
        }
      }
      return billingProfileDetail?.defaultPaymentMethod;
    },
    [availableBillingProfilesForCustomer, invoiceGroupDetails, selectedInvoiceGroupId]
  );

  const [associatedPaymentMethodNull, setAssociatedPaymentMethodNull] = useState(false);

  const isAssociatedPaymentMethodTypeIsPaypal = isPaypalPaymentMethod(
    checkForAssociatedPaymentMethod(invoiceGroupId)?.type
  );

  useEffect(() => {
    setAssociatedPaymentMethodNull(!checkForAssociatedPaymentMethod(selectedInvoiceGroupId || ''));
  }, [checkForAssociatedPaymentMethod, selectedInvoiceGroupId]);

  const orderTnCRef = useRef<HTMLDivElement>(null);
  const transferConsentRef = useRef<HTMLSpanElement>(null);

  const saveECCFailureMessage = () => {
    return <div>You can still update it in the billing profile section, or contact {getSupportUrl()}.</div>;
  };

  const contactServiceECCHandler = async () => {
    try {
      const existingAccountIds = endCustomerContacts?.map((contact) => contact.accountId);
      const createContactIds = atlassianAccountIds?.filter((id) => !existingAccountIds?.includes(id));
      let createOrPatchContactPayload;
      let createContactPayload;
      let primaryContactId = '';
      const areBothIdsPresent = quotePrimaryContactsAaids[0] !== '' && atlassianAccountIds[0] !== '';
      const areBothIdsSame =
        quotePrimaryContactsAaids[0] !== '' &&
        atlassianAccountIds[0] !== '' &&
        quotePrimaryContactsAaids[0] === atlassianAccountIds[0];
      if (quotePrimaryContactsAaids[0] !== '' && atlassianAccountIds[0] !== '') {
        primaryContactId = quotePrimaryContactsAaids[0];
      } else if (quotePrimaryContactsAaids[0] !== '' && atlassianAccountIds[0] === '') {
        primaryContactId = quotePrimaryContactsAaids[0];
      } else if (quotePrimaryContactsAaids[0] === '' && atlassianAccountIds[0] !== '') {
        primaryContactId = atlassianAccountIds[0];
      }

      const isAmongTheSecondaryContact =
        allSecondaryCustomerContacts?.some(
          (contact: ContactInfo) => contact?.identifierId === invoiceGroupId && contact?.accountId === primaryContactId
        ) ?? false;

      const hasExistingPrimaryContact = !!primaryContacts?.find((contact) => contact?.identifierId === invoiceGroupId);

      /** Handling different possible scenarios of quote primary contact and bp level primary contact: https://hello.atlassian.net/l/cp/9VQgBRK6 */

      if (shouldShowPrimaryContacts && hasExistingPrimaryContact && !areBothIdsSame && areBothIdsPresent) {
        if (!isAmongTheSecondaryContact) {
          createContactPayload = getCreateContactRelationshipsPayload({
            invoiceGroupId: invoiceGroupId,
            transactionAccountId: transactionAccountId,
            createContactIds: [primaryContactId],
          });
        }
        const existingPrimaryContactId = primaryContacts?.find((contact) => contact?.identifierId === invoiceGroupId)
          ?.accountId;
        createOrPatchContactPayload = getPatchPayloadForAddingAndRemovingPrimaryContact({
          invoiceGroupId: invoiceGroupId,
          transactionAccountId: transactionAccountId,
          newPrimaryContactId: primaryContactId as string,
          existingPrimaryContactId: existingPrimaryContactId as string,
        });
      } else if (shouldShowPrimaryContacts && isAmongTheSecondaryContact && !hasExistingPrimaryContact) {
        createOrPatchContactPayload = getPatchPayloadForSetPrimaryContact({
          invoiceGroupId: invoiceGroupId,
          transactionAccountId: transactionAccountId,
          primaryContactId: primaryContactId as string,
        });
      } else {
        createOrPatchContactPayload = getCreateContactRelationshipsPayload({
          invoiceGroupId: invoiceGroupId as string,
          transactionAccountId: transactionAccountId,
          createContactIds: shouldShowPrimaryContacts ? [primaryContactId] : createContactIds,
          ...(shouldShowPrimaryContacts && { primaryContactId: primaryContactId }),
        });
      }

      if (!isEmpty(createContactPayload)) {
        if (shouldShowPrimaryContacts && hasExistingPrimaryContact && !isAmongTheSecondaryContact && !areBothIdsSame) {
          await createContactRelationship(createContactPayload as CreateContactRelationshipsRequest);
        }
      }

      //create contacts if required
      if (!isEmpty(createOrPatchContactPayload)) {
        if (
          (shouldShowPrimaryContacts &&
            !hasAddedNewPrimaryContact &&
            isAmongTheSecondaryContact &&
            !hasExistingPrimaryContact) ||
          (shouldShowPrimaryContacts && hasExistingPrimaryContact && !areBothIdsSame && areBothIdsPresent) ||
          (shouldShowPrimaryContacts &&
            hasAddedNewPrimaryContact &&
            isAmongTheSecondaryContact &&
            !hasExistingPrimaryContact)
        ) {
          await patchContactRelationship(createOrPatchContactPayload as PatchContactRelationshipsRequest);
        } else await createContactRelationship(createOrPatchContactPayload as CreateContactRelationshipsRequest);
      }
      return true;
    } catch (err) {
      if (!notificationSent) {
        sendErrorNotification(
          errorNotificationBuilder()
            .withId('ecc-not-updated')
            .withHeader(
              "We're unable to update customer contact"
            ) /** need to check this with the content team when primary contacts FF is on */
            .withMessage(saveECCFailureMessage())
            .withDismissAfter(5000)
            .isVersion(NotificationTypes.FLAG)
            .build()
        );
        setNotificationSent(true);
      }
      return false;
    }
  };

  const handleSubmit = useCallback(async () => {
    const orderTnCText = orderTnCRef.current?.innerText ?? '';
    const transferConsentText = transferConsentRef.current?.innerText ?? '';
    const consentText = containsTransferLineItems ? orderTnCText + transferConsentText : orderTnCText;
    if (shouldShowPrimaryContacts) {
      setOrderDetails && setOrderDetails({ isProcessing: true, isPlaced: false, slug: '', orderId: '' });
      const isResponseSuccessful = await contactServiceECCHandler();
      isResponseSuccessful && handleOrderSubmit(consentText);
      !isResponseSuccessful &&
        setOrderDetails &&
        setOrderDetails({ isProcessing: false, isPlaced: false, slug: '', orderId: '' });
    } else {
      contactServiceECCHandler();
      handleOrderSubmit(consentText);
    }
  }, [atlassianAccountIds, containsTransferLineItems, handleOrderSubmit, contactServiceECCHandler]);

  const getFormattedDescription = (descriptionStr: string) => {
    if (!isEmpty(descriptionStr)) {
      return (
        <div>
          {descriptionStr} Contact {getSupportUrl()} for help.
        </div>
      );
    }
    return <div>Contact {getSupportUrl()} for help.</div>;
  };

  const errorToasts = (
    <FlagGroup onDismissed={() => setError(null)}>
      {error && (
        <AutoDismissFlag
          icon={<WarningIcon label="Warning" primaryColor={'red'} />}
          id={error.description}
          title={error.title}
          description={getFormattedDescription(error.description)}
        />
      )}
    </FlagGroup>
  );

  const isAllEndCustomerContactAdded = useMemo(() => !(atlassianAccountIds.findIndex((id) => !id) >= 0), [
    atlassianAccountIds,
  ]);

  const hasPrimayContactPresent = useMemo(
    () =>
      shouldShowPrimaryContacts
        ? quotePrimaryContactsAaids?.findIndex((id) => id) >= 0 || atlassianAccountIds?.findIndex((id) => id) >= 0
        : true,
    [quotePrimaryContactsAaids, atlassianAccountIds]
  );

  const isOrderConfirmationDisabled = useMemo(
    () =>
      invoiceGroupDetailsLoading ||
      (containsTransferLineItems && !transferConsent) ||
      (!shouldShowPrimaryContacts && !isCustomerContactHidden && !isAllEndCustomerContactAdded) ||
      termPaymentValidation.isLoading ||
      isDeferredPaymentIGSelectionBlocked ||
      !hasPrimayContactPresent ||
      isPendingInvoice ||
      areEstimatesRefreshing ||
      associatedPaymentMethodNull ||
      isDeactivatedBillingProfileSelected ||
      isPollingFailed ||
      (!isPaypalEnabled && isAssociatedPaymentMethodTypeIsPaypal),
    [
      invoiceGroupDetailsLoading,
      transferConsent,
      isCustomerContactHidden,
      isAllEndCustomerContactAdded,
      areEstimatesRefreshing,
      isDeferredPaymentIGSelectionBlocked,
      isPendingInvoice,
      containsTransferLineItems,
      associatedPaymentMethodNull,
      hasPrimayContactPresent,
      isDeactivatedBillingProfileSelected,
      termPaymentValidation.isLoading,
      isPollingFailed,
      isPaypalEnabled,
      isAssociatedPaymentMethodTypeIsPaypal,
    ]
  );
  const labelBillingProfile = () => {
    return <label className="billing-profile-container-text-label">Billing profile</label>;
  };
  const {
    isOpen: showBillingProfileAndPONumberModal,
    open: openBillingProfileAndPONumberModal,
    close: closeBillingProfileAndPONumberModal,
  } = useOpenClose();
  const [billingProfileAction, setBillingProfileAction] = useState<BillingProfileActionsEnum>();
  const [isUpdatingPONumberFailed, setIsUpdatingPONumberFailed] = useState(false);
  const [updatingPONumber, setUpdatingPONumber] = useState<boolean>(false);

  const handleOnPONumberChange = useCallback(() => {
    setBillingProfileAction(BillingProfileActionsEnum.CHANGE_PO_NUMBER);
    setIsUpdatingPONumberFailed(false);
    openBillingProfileAndPONumberModal();
  }, [openBillingProfileAndPONumberModal]);

  const handleOnBillingProfileChange = useCallback(() => {
    setBillingProfileAction(BillingProfileActionsEnum.CHANGE_BILLING_PROFILE);
    setIsUpdatingPONumberFailed(false);
    openBillingProfileAndPONumberModal();
  }, [openBillingProfileAndPONumberModal]);

  const handleOnSavePONumber = useCallback(
    async (PONumber: string, selectedBillingProfile: ValueType<BillingProfileOption>) => {
      setUpdatingPONumber(true);
      const billingProfileDetails = availableBillingProfilesForCustomer?.find(
        (billingProfile) => billingProfile?.id === selectedBillingProfile?.value
      );
      if (selectedBillingProfile?.value && billingProfileDetails) {
        try {
          const requestData: UpdateInvoiceGroupRequest = {
            data: {
              name: billingProfileDetails.name,
              shipToPartyId: billingProfileDetails.shipToParty?.id,
              purchaseOrder: {
                number: PONumber,
                oneTimeUse: true, // always pass one time use for PO change on Quote to order
              },
            },
            invoiceGroupId: selectedBillingProfile?.value,
            transactionAccountId: transactionAccountId,
          };
          await updateInvoiceGroup(requestData);
          setIsUpdatingPONumberFailed(false);
          closeBillingProfileAndPONumberModal();
          refetchbillingProfilesForCustomer();
          return 'success';
        } catch (e) {
          setIsUpdatingPONumberFailed(true);
        } finally {
          setUpdatingPONumber(false);
        }
      }
      return 'failed';
    },
    [
      availableBillingProfilesForCustomer,
      closeBillingProfileAndPONumberModal,
      refetchbillingProfilesForCustomer,
      transactionAccountId,
    ]
  );

  const handleBillingProfileSave = useCallback(
    async (
      selectedBillingProfile: ValueType<BillingProfileOption>,
      PONumber: string,
      atlassianAccountIds?: string[],
      atlassianAccountEmails?: string[],
      hasAddedPrimaryContactInTheModal?: boolean
      /* eslint-disable max-depth */
      /* eslint-disable max-params */
    ) => {
      if (selectedBillingProfile?.value) {
        resetFinancePolicies();
        const isPaymentMethodNullForIG = !checkForAssociatedPaymentMethod(selectedBillingProfile?.value);
        if (!isPaymentMethodNullForIG) {
          if (!(billingProfileAction === BillingProfileActionsEnum.CHANGE_BILLING_PROFILE)) {
            const result = await handleOnSavePONumber(PONumber, selectedBillingProfile);
            if (result === 'success') {
              if (shouldShowPrimaryContacts) {
                atlassianAccountIds && setAtlassianAccountIds(atlassianAccountIds);
                atlassianAccountEmails && setAtlassianAccountEmails(atlassianAccountEmails);
                setHasAddedPrimaryContactInTheModal(!!hasAddedPrimaryContactInTheModal);
                setShouldReRenderBillingProfile(true);
              }
              setInvoiceGroupId(selectedBillingProfile?.value);
              setAssociatedPaymentMethodNull(false);
              closeBillingProfileAndPONumberModal();
            }
          } else if (billingProfileAction === BillingProfileActionsEnum.CHANGE_BILLING_PROFILE) {
            if (shouldShowPrimaryContacts) {
              atlassianAccountIds && setAtlassianAccountIds(atlassianAccountIds);
              atlassianAccountEmails && setAtlassianAccountEmails(atlassianAccountEmails);
              setHasAddedPrimaryContactInTheModal(!!hasAddedPrimaryContactInTheModal);
              setShouldReRenderBillingProfile(true);
            }
            setInvoiceGroupId(selectedBillingProfile?.value);
            setAssociatedPaymentMethodNull(false);
            closeBillingProfileAndPONumberModal();
          }
        } else {
          if (shouldShowPrimaryContacts) {
            atlassianAccountIds && setAtlassianAccountIds(atlassianAccountIds);
            atlassianAccountEmails && setAtlassianAccountEmails(atlassianAccountEmails);
            setHasAddedPrimaryContactInTheModal(!!hasAddedPrimaryContactInTheModal);
            setShouldReRenderBillingProfile(true);
          }
          setInvoiceGroupId(selectedBillingProfile?.value);
          setAssociatedPaymentMethodNull(true);
          closeBillingProfileAndPONumberModal();
        }
      }
    },
    [
      billingProfileAction,
      checkForAssociatedPaymentMethod,
      closeBillingProfileAndPONumberModal,
      handleOnSavePONumber,
      resetFinancePolicies,
      setInvoiceGroupId,
    ]
  );

  const handleBillingProfileCancel = useCallback(async (hasAddedPrimaryContactInTheModal?: boolean) => {
    shouldShowPrimaryContacts &&
      hasAddedPrimaryContactInTheModal &&
      setHasAddedPrimaryContactInTheModal(hasAddedPrimaryContactInTheModal);
    closeBillingProfileAndPONumberModal();
  }, []);

  const billingProfileActions: MoreActionsItem[] = [
    {
      id: 'po_number',
      content: 'PO Number',
      isDisabled: associatedPaymentMethodNull,
      action: () => handleOnPONumberChange(),
    },
    {
      id: 'change_billing_profile',
      content: 'Change',
      action: () => handleOnBillingProfileChange(),
    },
  ];

  const BillingProfileMoreActions = <MoreActions actions={billingProfileActions} />;

  isAddNewContactDisabled = atlassianAccountIds.length >= getMaxLimitOfCustomerContact();

  const renderECCFailureMessage = () => {
    return <div>Try again or contact {getSupportUrl()}.</div>;
  };

  const showECCFetchFailedMessage = () => {
    if (!notificationSent) {
      const notificationOptions = errorNotificationBuilder()
        .isVersion(NotificationTypes.FLAG)
        .withId('customer-contact-fetch-error')
        .withHeader("We're unable to load customer contacts")
        .withMessage(renderECCFailureMessage())
        .withDismissAfter(5000)
        .build();
      sendErrorNotification(notificationOptions);
      setNotificationSent(true);
    }
  };

  if (eccDetailsError) {
    showECCFetchFailedMessage();
  }

  const isBillingProfileSectionLoading = billingProfilesLoading || invoiceGroupDetailsLoading;

  const shouldShowProvisionSiteCTA = () => {
    if (isFutureDatedEntitlementsEnabled) {
      /**
       * for future dated net new entitlements, lineItemType will be CREATE_ENTITLEMENT and startsAt timestamp will be a non null value,
       * in that case we don't want to show provisioning CTA as the entitlement will start in future.
       * */
      return (
        !lineItems?.find((item) => item?.lineItemType === QUOTE_LINE_ITEM_TYPES.REACTIVATE_ENTITLEMENT) &&
        lineItems.some((item) => {
          return item.lineItemType === QUOTE_LINE_ITEM_TYPES.CREATE_ENTITLEMENT && !item?.startsAt?.timestamp;
        })
      );
    }
    return lineItems.some((item) => item.lineItemType === QUOTE_LINE_ITEM_TYPES.CREATE_ENTITLEMENT);
  };

  const lineItemsCommonProps = {
    loading: areEstimatesRefreshing,
    isAddOnChangesEnabled: isAddOnChangesEnabled,
    isProratedChangesEnabled: isProratedChangesEnabled,
    currency: defaultCurrency,
    isDesignIteration1Enabled: isDesignIteration1Enabled,
    isTWCPhase1Enabled: isTWCPhase1Enabled,
    isMultiInstanceAppsEnabled: isMultiInstanceAppsEnabled,
  };

  return (
    <div className="quote-order-review-container" data-testid="ppc-order-summary">
      <ScreenEventAnalytics
        event={{
          name: AnalyticsEventSource.PARTNER_QUOTE_ORDER_SUMMARY_SCREEN,
        }}
      />
      {errorToasts}
      {orderDetails?.isPlaced ? (
        <OrderConfirmationDrawer
          orderSlug={orderDetails?.slug ?? ''}
          newSiteOrderId={orderDetails?.orderId ?? ''}
          transactionAccountId={transactionAccountId}
          showProvisionSiteCTA={shouldShowProvisionSiteCTA()}
          onGetStarted={(link: string) => {
            window.open(link);
          }}
        />
      ) : (
        <>
          <div className="quote-order-review-container-summary-header">
            <h1>Order summary</h1>
            <p className="quote-order-review-container-summary-header-sub">
              Review the subscription and billing details before confirming your order for Quote - {quoteNumber}.
            </p>
          </div>
          <div className="quote-order-review-container-review-summary">
            <div className="quote-order-review-container-review-summary-details">
              <div className="quote-order-review-container-review-summary-details-line-items-container">
                <div className="quote-order-review-container-review-summary-details-line-items-container-line-items">
                  <>
                    {Object.entries(groupLineItemsByOrgName).map(([orgName, lineItems], index) => {
                      const jiraLineItems = lineItems.filter((lineItem) => lineItem.isItAJiraProduct);
                      const nonJiraLineItems = lineItems.filter((lineItem) => !lineItem.isItAJiraProduct);
                      return (
                        <CustomPanel
                          heading={`Organization: ${orgName}`}
                          toggleButtonOnLeft
                          key={orgName}
                          isDefaultExpanded={index === 0}
                          testId="quotes-line-items-order-summary"
                          iconBefore={<OfficeBuildingFilledIcon label="" />}
                        >
                          <LineItems lineItems={jiraLineItems} isJiraFamily={true} {...lineItemsCommonProps} />
                          {jiraLineItems && jiraLineItems.length > 0 && nonJiraLineItems.length > 0 && <Divider />}

                          <LineItems lineItems={nonJiraLineItems} isJiraFamily={false} {...lineItemsCommonProps} />
                        </CustomPanel>
                      );
                    })}
                    {Object.entries(groupedLineItemsByCloudsite).map(([cloudSiteName, lineItems], index) => {
                      const jiraLineItems = lineItems.filter((lineItem) => lineItem.isItAJiraProduct);
                      const nonJiraLineItems = lineItems.filter((lineItem) => !lineItem.isItAJiraProduct);
                      return (
                        <CustomPanel
                          heading={cloudSiteName}
                          toggleButtonOnLeft
                          key={cloudSiteName}
                          isDefaultExpanded={index === 0}
                          testId="quotes-line-items-order-summary"
                        >
                          <LineItems lineItems={jiraLineItems} isJiraFamily={true} {...lineItemsCommonProps} />
                          {jiraLineItems && jiraLineItems.length > 0 && nonJiraLineItems.length > 0 && <Divider />}

                          <LineItems lineItems={nonJiraLineItems} isJiraFamily={false} {...lineItemsCommonProps} />
                        </CustomPanel>
                      );
                    })}
                  </>
                </div>
                <div className="quote-order-review-container-review-summary-details-line-items-container-total-estimate-summary">
                  <div className="quote-order-review-container-row-container">
                    <h5 className="quote-order-review-container-review-summary-details-line-items-container-total-estimate-summary-total-estimate-label">
                      Subtotal
                    </h5>
                    <Estimate
                      currency={defaultCurrency}
                      price={lineItemsActualEstimate?.totalExcludingDiscountAndTax}
                      isTotal={true}
                      loading={areEstimatesRefreshing}
                      className={
                        'quote-order-review-container-review-summary-details-line-items-container-total-estimate-summary-total-estimate-currency'
                      }
                    />
                  </div>
                  {lineItemsActualEstimate?.totalAdminFee ? (
                    <div className="quote-order-review-container-row-container">
                      <div className="quote-order-review-container-row-container tax-gap">
                        <p className="selected-app-pricing-details">Total administration fee</p>
                      </div>
                      <p className="selected-app-pricing-details"></p>
                      <div className="quote-order-review-container-row-container tax-gap">
                        <Estimate
                          currency={defaultCurrency}
                          price={lineItemsActualEstimate?.totalAdminFee}
                          className={
                            'quote-order-review-container-review-summary-details-line-items-container-total-estimate-summary-sub-title-pricing-currency'
                          }
                          loading={areEstimatesRefreshing}
                        />
                      </div>
                    </div>
                  ) : null}
                  {!areAllTheAddedProductsFree && (
                    <div className="quote-order-review-container-row-container">
                      <div className="quote-order-review-container-row-container tax-gap">
                        <p className="selected-app-pricing-details">Tax estimate</p>
                      </div>
                      <p className="selected-app-pricing-details"></p>
                      <div className="quote-order-review-container-row-container tax-gap">
                        <Estimate
                          currency={defaultCurrency}
                          price={lineItemsActualEstimate?.totalTax}
                          className={
                            'quote-order-review-container-review-summary-details-line-items-container-total-estimate-summary-sub-title-pricing-currency'
                          }
                          loading={areEstimatesRefreshing}
                        />
                      </div>
                    </div>
                  )}
                  {
                    <div className="quote-order-review-container-row-container">
                      <div className="quote-order-review-container-row-container tax-gap">
                        <p className="selected-app-pricing-details">Total discount</p>
                      </div>
                      <p className="selected-app-pricing-details"></p>
                      <div className="quote-order-review-container-row-container tax-gap">
                        <Estimate
                          currency={defaultCurrency}
                          price={
                            lineItemsActualEstimate?.totalDiscountApplied
                              ? -lineItemsActualEstimate?.totalDiscountApplied
                              : 0
                          }
                          className={
                            'quote-order-review-container-review-summary-details-line-items-container-total-estimate-summary-sub-title-pricing-currency'
                          }
                          loading={areEstimatesRefreshing}
                        />
                      </div>
                    </div>
                  }
                  <div className="quote-order-review-container-row-container">
                    <p className="selected-app-pricing-details">Total partner discount</p>
                    <p className="selected-app-pricing-details"></p>
                    <Estimate
                      currency={defaultCurrency}
                      price={
                        lineItemsActualEstimate?.totalPartnerDiscount
                          ? -lineItemsActualEstimate?.totalPartnerDiscount
                          : 0
                      }
                      className={
                        'quote-order-review-container-review-summary-details-line-items-container-total-estimate-summary-sub-title-pricing-currency'
                      }
                      loading={areEstimatesRefreshing}
                    />
                  </div>
                  <Divider className="mt-16 mb-16" />
                  <div className="quote-order-review-container-row-container">
                    <h5 className="quote-order-review-container-review-summary-details-line-items-container-total-estimate-summary-grand-total-estimate">
                      Immediate total
                    </h5>
                    <Estimate
                      currency={defaultCurrency}
                      price={lineItemsActualEstimate?.totalAmountToPay}
                      isTotal={true}
                      loading={areEstimatesRefreshing}
                      className={
                        'quote-order-review-container-review-summary-details-line-items-container-total-estimate-summary-grand-total-currency'
                      }
                    />
                  </div>
                </div>
              </div>
              <div className="place-order-actions-container">
                {!invoiceGroupDetailsLoading && !billingProfilesLoading && invoiceGroupDetails && (
                  <BillingProfileAndPONumberModal
                    isOpen={showBillingProfileAndPONumberModal}
                    onClose={handleBillingProfileCancel}
                    preSelectedBillingProfile={selectedBillingProfileDetails}
                    billingProfileAction={billingProfileAction}
                    billingProfilesInfo={availableBillingProfilesForCustomer}
                    isUpdatingPONumberFailed={isUpdatingPONumberFailed}
                    setIsUpdatingPONumberFailed={setIsUpdatingPONumberFailed}
                    updatingPONumber={updatingPONumber}
                    onSavePONumber={handleOnSavePONumber}
                    onSave={handleBillingProfileSave}
                    checkFinancePolicies={checkFinancePolicies}
                    isCheckingFinancePolicies={termPaymentValidation.isLoading}
                    isDeferredPaymentMethodDisabled={false}
                    // FIXME :: Enable this in quotes as well
                    isNoPaymentMethodDisabled={false}
                    showPricingEligibility={isNonCommercialPricingEnable}
                    transactionAccountId={transactionAccountId}
                    showPrimaryContacts={shouldShowPrimaryContacts}
                    primaryContacts={primaryContacts}
                    secondaryCustomerContacts={allSecondaryCustomerContacts as any}
                    atlassianAccountIds={primaryContactsAaidForBp}
                    setAtlassianAccountIds={setPrimaryContactsAaidForBp}
                    atlassianAccountEmails={primaryContactEmailsForBp}
                    setAtlassianAccountEmails={setPrimaryContactEmailsForBp}
                    hasAddedPrimaryContactInTheModal={hasAddedPrimaryContactInTheModal}
                    isPayPalEnabled={isPaypalEnabled}
                  />
                )}
                <Form onSubmit={handleSubmit}>
                  {({ formProps }) => {
                    return (
                      <form {...formProps}>
                        <div className="place-order-actions-container-actions">
                          {!areAllTheAddedProductsFree && (
                            <div
                              className={`place-order-actions-container-order-info-selects`}
                              data-testid="ppc-order-info-selects-container"
                            >
                              <div>
                                <label className="quote-order-review-container-text-label">Billing address</label>
                                <div className="commerce-address-container">
                                  <div className="selected-commerce-address-container">
                                    <FormatCommerceAddress commerceAddress={billToAddressData} />
                                  </div>
                                </div>
                              </div>
                              {shouldReRenderBp && (
                                <>
                                  <BillingProfile
                                    isLoading={isBillingProfileSectionLoading}
                                    billingProfileInfo={billingProfileInfo}
                                    invoiceGroupName={billingProfileInfo?.name || ''}
                                    Label={labelBillingProfile}
                                    BillingProfileMoreActions={BillingProfileMoreActions}
                                    displayPONumber={true}
                                    atlassianAccountIds={atlassianAccountIds}
                                    setAtlassianAccountIds={setAtlassianAccountIds}
                                    atlassianAccountEmails={atlassianAccountEmails}
                                    setAtlassianAccountEmails={setAtlassianAccountEmails}
                                    eccLoading={eccLoading}
                                    isAddNewContactDisabled={areEstimatesRefreshing || isAddNewContactDisabled}
                                    isExistingContactRemoveDisabled={
                                      areEstimatesRefreshing || isExistingContactRemoveDisabled
                                    }
                                    currentUserList={atlassianAccountIds}
                                    showPricingEligibility={isNonCommercialPricingEnable}
                                    showPrimaryCustomerContacts={shouldShowPrimaryContacts}
                                    quotePrimaryContactsAaids={quotePrimaryContactsAaids}
                                    setQuotePrimaryContactsAaids={setQuotePrimaryContactsAaids}
                                    quotePrimaryContactsEmails={quotePrimaryContactsEmails}
                                    setQuotePrimaryContactsEmails={setQuotePrimaryContactsEmails}
                                    isQuotePrimaryContactsLoading={isQuotePrimaryContactsLoading}
                                    quotePrimaryContactName={quotePrimaryContactName}
                                    showQuotePrimaryContact={shouldShowPrimaryContacts}
                                    secondaryCustomerContacts={allSecondaryCustomerContacts}
                                    isBulkECCDetailsLoading={bulkEccDetailsLoading}
                                    shouldShowACHPaymentMethod={isAchPaymentMethodFFEnabled}
                                  />
                                  {!isBillingProfileSectionLoading && associatedPaymentMethodNull ? (
                                    <ErrorMessage>
                                      {'Payment method missing in the selected billing profile'}
                                    </ErrorMessage>
                                  ) : null}
                                </>
                              )}
                              {isDeactivatedBillingProfileSelected && !isBillingProfileSectionLoading && (
                                <div className="error-section-message">
                                  <SectionMessage title={`We're unable to use this billing profile`} appearance="error">
                                    {
                                      'This billing profile is deactivated. To place the order, switch to a valid billing profile.'
                                    }
                                  </SectionMessage>
                                </div>
                              )}
                              {isDeferredPaymentIGSelectionBlocked &&
                                !isBillingProfileSectionLoading &&
                                termPaymentValidation &&
                                termPaymentValidation.response && (
                                  <div className="error-section-message">
                                    <TermPaymentValidationError
                                      financePolicyResponse={termPaymentValidation.response}
                                      transactionAccountId={transactionAccountId}
                                    />
                                  </div>
                                )}
                              {isPendingInvoice && !isBillingProfileSectionLoading && !associatedPaymentMethodNull && (
                                <div className="error-section-message">
                                  <SectionMessage title={`We're processing your invoice`} appearance="warning">
                                    {
                                      'You can place a new order once the previous invoice for this billing profile is generated'
                                    }
                                  </SectionMessage>
                                </div>
                              )}
                              <div className="actions-container">
                                {!areAllTheAddedProductsFree && (
                                  <div ref={orderTnCRef}>
                                    <p className="order-info">
                                      By confirming the order, and in accordance with the order section of the partner
                                      agreement executed between You and Atlassian, you agree and acknowledge to be
                                      bound by the partner agreement, and acknowledge the{' '}
                                      <a href={legalLinks.privacyPolicyUrl} target="_blank">
                                        Privacy Policy.
                                      </a>{' '}
                                      Except where you have a current agreement with Atlassian, the terms of such
                                      agreement shall supersede the{' '}
                                      <a href={legalLinks.customerAgreementUrl} target="_blank">
                                        Atlassian Customer Agreement.
                                      </a>{' '}
                                      In the event of any conflict between the terms of your current agreement and the{' '}
                                      <a href={legalLinks.customerAgreementUrl} target="_blank">
                                        Atlassian Customer Agreement
                                      </a>
                                      , the terms of your current agreement shall prevail.
                                    </p>
                                    <p className="order-info">
                                      You are responsible for ensuring each customer has entered into a customer
                                      agreement, at or before such customer&apos;s purchase or use of the offerings, in
                                      a manner that is legally binding upon the customer.
                                    </p>
                                    <p className="order-info">
                                      You agree to the details, price, frequency, and other adjustments presented here,
                                      and understand that if your customer adjusts the user numbers, the price will be
                                      adjusting accordingly. After any trial periods, we&apos;ll invoice you with the
                                      final amount. You may first be charged a prorated amount to align your bill date
                                      with other subscriptions.
                                    </p>
                                    <p className="order-info">
                                      Our prices may change upon notice to you. Your customer contact can cancel the
                                      subscription anytime by going to{' '}
                                      <a href={getPartnerSubscriptionsUrl()}>{getPartnerSubscriptionsUrl()}</a> and
                                      following the steps.
                                    </p>
                                  </div>
                                )}
                                {containsTransferLineItems && (
                                  <CheckboxField name="ppc-order-review-tnc" isRequired={true}>
                                    {({ fieldProps }) => (
                                      <Checkbox
                                        {...fieldProps}
                                        label={
                                          <span ref={transferConsentRef}>
                                            The customer has consented to transferring the subscriptions to your billing
                                            account.
                                          </span>
                                        }
                                        isChecked={transferConsent}
                                        onChange={() => {
                                          setTransferConsent((prevState) => {
                                            return !prevState;
                                          });
                                        }}
                                        testId={'tnc-checkbox'}
                                      />
                                    )}
                                  </CheckboxField>
                                )}

                                <div className="actions-footer">
                                  <AdvocateAccessWall
                                    accessType={ADVOCATE_ACCESS_TYPE.CREATE}
                                    resource={ADVOCATE_RESOURCE_TYPE.ORDER}
                                  >
                                    <LoadingButton
                                      type="submit"
                                      appearance="primary"
                                      testId={'ppc-order-review.place-order'}
                                      isLoading={orderDetails?.isProcessing}
                                      isDisabled={isOrderConfirmationDisabled}
                                    >
                                      Confirm
                                    </LoadingButton>
                                  </AdvocateAccessWall>
                                  <Button appearance="subtle" onClick={() => handleBackButtonClick()}>
                                    Back
                                  </Button>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </form>
                    );
                  }}
                </Form>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
